import { createContext, useContext, useState, ReactNode, useEffect, useMemo } from "react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

interface CartItem {
  articleId: number;
  name: string;
  price: number;
  quantity: number;
  sizeColorId?: number;
  sizeName?: string;
  colorName?: string;
  imageUrl?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void;
  removeItem: (articleId: number, sizeColorId?: number) => void;
  updateQuantity: (articleId: number, quantity: number, sizeColorId?: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(items));
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
    }
  }, [items]);

  const addItem = (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(
        i => i.articleId === item.articleId && i.sizeColorId === item.sizeColorId
      );
      
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex].quantity += item.quantity || 1;
        toast.success("Antall oppdatert i handlekurv");
      } else {
        toast.success("Lagt til i handlekurv");
        
        // Confetti animation on first add
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.8 },
          colors: ['#10b981', '#3b82f6', '#8b5cf6'],
        });
      }
      
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex].quantity += item.quantity || 1;
        return newItems;
      }
      
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
  };

  const removeItem = (articleId: number, sizeColorId?: number) => {
    setItems(prev => prev.filter(
      item => !(item.articleId === articleId && item.sizeColorId === sizeColorId)
    ));
    toast.success("Fjernet fra handlekurv");
  };

  const updateQuantity = (articleId: number, quantity: number, sizeColorId?: number) => {
    if (quantity <= 0) {
      removeItem(articleId, sizeColorId);
      return;
    }
    
    setItems(prev => prev.map(item =>
      item.articleId === articleId && item.sizeColorId === sizeColorId
        ? { ...item, quantity }
        : item
    ));
  };

  const clearCart = () => {
    setItems([]);
    toast.success("Handlekurv tømt");
  };

  const totalItems = useMemo(() => 
    items.reduce((sum, item) => sum + item.quantity, 0), 
    [items]
  );
  
  const totalPrice = useMemo(() => 
    items.reduce((sum, item) => sum + (item.price * item.quantity), 0), 
    [items]
  );

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      totalItems,
      totalPrice
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
}
